// Models (02-DESIGN/05-inference/01-models-and-instances.md § the console:
// Models; 01-the-console.md § the surfaces, § the words: *a model*, *provider*
// and *model*): one row per model — the display name, under it the provider
// and model — with its status; a row opens the model's page; *Add a model*
// a form in the list's place composing the three verbs, and a row's *Repoint*
// and *New key* forms there too (01-the-console.md § conventions: a form). For an admin, since what models exist is the workspace's
// decision. Discovery is the catalogue and runs on every visit; the callout
// discovery runs beside it only when a model's required document names a
// callout, whose name the status then carries.

import { identity } from "@impire-io/sdk";
import { Plus } from "lucide-react";
import { useState } from "react";

import { tenantPath } from "../../address/address.ts";
import { usePage } from "../../platform/session.tsx";
import { Button, Empty, NotAllowed, Notice, PageHead, Pending, Pill, useLoad } from "../../ui/components.tsx";
import { listSubject, RegistrationState, SERVICE, type ListReply } from "../../wire/workloads.ts";
import { Table, type Column } from "../../ui/table.tsx";
import { useFormState } from "../../ui/form.tsx";
import { useSurface } from "../surface.tsx";
import { AddModel } from "./AddModel.tsx";
import { NewKey, removeConsequence, removeModel, Repoint } from "./ModelPage.tsx";
import { assemble, isModelWorkload, needsCallouts, readFacts, type Model } from "./models.ts";

export function ModelsPage() {
  const s = useSurface();
  const { platform } = usePage();
  const image = platform.images?.[identity.Image.inference] || undefined;
  // Which form shows in the list's place: the add, or a row's Repoint or New key by the model's id.
  const form = useFormState<ModelsForm>();
  const [mark, setMark] = useState<string | undefined>(undefined);
  const [err, setErr] = useState<unknown>(undefined);
  const [note, setNote] = useState<string | undefined>(undefined);
  const [rows, reload] = useLoad("models", async () => {
    const [list, instances] = await Promise.all([
      s.bus.call<ListReply>(SERVICE, listSubject(), undefined),
      s.bus.models().catch(() => []),
    ]);
    const discovered = new Set(instances.map((i) => i.workload));
    const models = list.workloads.filter(
      (w) => w.state !== RegistrationState.retired && isModelWorkload(w, image, discovered),
    );
    const facts = new Map(
      await Promise.all(models.map(async (w) => [w.id, await readFacts(s.bus, w.id)] as const)),
    );
    // A callout is looked for only when a model's required document names one.
    const callouts = needsCallouts(facts.values())
      ? await s.bus.callouts().catch(() => undefined)
      : undefined;
    return assemble(models, facts, instances, callouts);
  });

  const add =
    image !== undefined ? (
      <Button
        kind="primary"
        onClick={() => {
          form.show("add");
        }}
      >
        <Plus strokeWidth={1.8} aria-hidden="true" />
        Add a model
      </Button>
    ) : undefined;
  if (form.open === "add" && image !== undefined) {
    return (
      <AddModel
        image={image}
        onClose={form.close}
        onRegistered={() => {
          reload();
        }}
        onAdded={(id) => {
          // Back to the list, the new model marked, shown as starting (01-the-console.md § conventions: add).
          form.close();
          setMark(id);
          reload();
        }}
      />
    );
  }
  const target = formTarget(form.open);
  const m =
    target !== undefined && rows.state === "ready" ? rows.value.find((r) => r.w.id === target.id) : undefined;
  if (target?.kind === "repoint" && m?.settings !== undefined) {
    return (
      <Repoint
        id={m.w.id}
        m={m}
        back="Models"
        onClose={form.close}
        onSaved={(what) => {
          form.close();
          setMark(m.w.id);
          setNote(what);
          reload();
        }}
      />
    );
  }
  if (target?.kind === "key" && m !== undefined) {
    return (
      <NewKey
        id={m.w.id}
        back="Models"
        onClose={form.close}
        onStored={(what) => {
          form.close();
          setMark(m.w.id);
          setNote(what);
          reload();
        }}
      />
    );
  }
  return (
    <>
      <PageHead
        title="Models"
        line={`The models ${s.name}'s apps and workloads can ask for, and who answers each.`}
        action={add}
      />
      {image === undefined && (
        <Notice title="No model image on this install">
          This install does not publish an image for models, so none can be added here. Ask whoever runs the
          install.
        </Notice>
      )}
      {rows.state === "pending" && <Pending what="Asking which models run" />}
      {rows.state === "refused" && <NotAllowed err={rows.err} what="Models" />}
      {rows.state === "ready" && rows.value.length === 0 && (
        <Empty action={add}>
          No models yet. Add one: name it, say which provider&apos;s model answers it, paste the key, and apps
          can ask for it by name.
        </Empty>
      )}
      {rows.state === "ready" && rows.value.length > 0 && (
        <Table
          label="Models"
          rows={rows.value}
          rowKey={(m) => m.w.id}
          sort={{ key: "model", dir: "asc" }}
          to={(m) => tenantPath(s.tenant, "models", m.w.id)}
          columns={columns}
          mark={mark}
          menu={(m) => [
            ...(m.settings !== undefined
              ? [
                  {
                    label: "Repoint",
                    onSelect: () => {
                      form.show(`repoint:${m.w.id}`);
                    },
                  },
                ]
              : []),
            {
              label: "New key",
              onSelect: () => {
                form.show(`key:${m.w.id}`);
              },
            },
            {
              label: "Remove",
              danger: true as const,
              confirm: { title: `Remove model ${m.displayName}?`, consequence: removeConsequence(m) },
              onSelect: () => {
                setErr(undefined);
                removeModel(s.bus, m.w.id).then(reload, (e: unknown) => {
                  setErr(e);
                  reload();
                });
              },
            },
          ]}
        />
      )}
      {err !== undefined && <NotAllowed err={err} />}
      {note !== undefined && <Notice kind="ok">{note}</Notice>}
    </>
  );
}

/** ModelsForm names the form the Models page shows in its list's place: the add, or a row's Repoint or New key, by the model's id. */
export type ModelsForm = "add" | `repoint:${string}` | `key:${string}`;

/** formTarget reads a row's form from its name: which form, for which model; nothing for the add or no form. */
export function formTarget(open: string | undefined): { kind: "repoint" | "key"; id: string } | undefined {
  if (open === undefined) {
    return undefined;
  }
  const at = open.indexOf(":");
  const kind = open.slice(0, at);
  const id = open.slice(at + 1);
  return at > 0 && id !== "" && (kind === "repoint" || kind === "key") ? { kind, id } : undefined;
}

/** The list's columns: the display name with the provider and model under it, the name apps ask for, the status. */
const columns: Column<Model>[] = [
  {
    key: "model",
    header: "Model",
    width: "minmax(0, 1.6fr)",
    value: (m) => m.displayName,
    text: (m) => `${m.displayName} ${m.provider} ${m.model}`,
    cell: (m) => (
      <span className="cell">
        <span className="cell-main">{m.displayName}</span>
        <span className="cell-sub">
          {m.provider !== "" || m.model !== "" ? `${m.provider} · ${m.model}` : "no settings yet"}
        </span>
      </span>
    ),
  },
  {
    key: "name",
    header: "Apps ask for",
    narrow: false,
    value: (m) => m.name,
    cell: (m) => (
      <span className="mono small">{m.name !== "" ? m.name : <span className="faint">—</span>}</span>
    ),
  },
  {
    key: "status",
    header: "Status",
    width: "140px",
    facet: "Status",
    value: (m) => m.status.word,
    cell: (m) =>
      m.callout !== undefined && m.status.beside !== undefined ? (
        <span className="cell">
          <Pill tone={m.status.tone}>{m.status.word}</Pill>
          <span className="cell-sub">
            {m.status.tone === "bad" ? "not running: " : "waiting for "}
            <span className="mono">{m.callout.name}</span>
          </span>
        </span>
      ) : (
        <Pill tone={m.status.tone}>{m.status.word}</Pill>
      ),
  },
];
